import React, { useCallback, useState } from 'react';

import PropTypes from 'prop-types';

import { useFetch } from 'hooks';

// Components
import { Mui } from 'components';

// Local components
import * as api from './api';
import AlertItem from './components/AlertItem';

const useStyles = Mui.makeStyles(theme => ({
  root: {
    width: '100%',
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    zIndex: theme.zIndex.appBar - 1,
  },
}));

function CommunicationAlerts({ type, slug }) {
  // Hooks
  const classes = useStyles();

  // States
  const [expanded, setExpanded] = useState(false);

  // Fetch
  const { data, error, mutate } = useFetch(
    [type && slug ? api.endpoints?.[type]?.list(slug) : null],
    {
      revalidateOnFocus: true,
      dedupingInterval: 30 * 1000,
      refreshInterval: 10 * 60 * 1000,
    },
  );

  // Constants
  const alerts = Array.isArray(data?.results) ? data.results : [];
  const [firstAlert, ...restAlerts] = alerts;
  const hasMore = restAlerts?.length > 0;

  // Actions
  const handleInteraction = useCallback(
    (uid, action) =>
      api.sendInteraction({ type, slug, objectUid: uid, action }).then(() => mutate()),
    [type, slug, mutate],
  );

  // Render
  if (type !== 'sellers' && type !== 'institutions') return null;
  if (!slug) return null;
  if (error || alerts.length === 0) return null;

  return (
    <div className={classes.root}>
      <AlertItem
        type={type}
        slug={slug}
        alert={firstAlert}
        isFirst
        hasMore={hasMore}
        expanded={expanded}
        onToggleExpand={() => setExpanded(prev => !prev)}
        onInteraction={handleInteraction}
        totalRest={restAlerts.length}
      />
      {hasMore && (
        <Mui.Collapse in={expanded} unmountOnExit>
          {restAlerts.map(alert => (
            <AlertItem
              key={alert.uid}
              type={type}
              slug={slug}
              alert={alert}
              isFirst={false}
              onInteraction={handleInteraction}
            />
          ))}
        </Mui.Collapse>
      )}
    </div>
  );
}

CommunicationAlerts.propTypes = {
  type: PropTypes.oneOf(['sellers', 'institutions']).isRequired,
  slug: PropTypes.string,
};

export default CommunicationAlerts;
